import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Card,
  Row,
  Col,
  Form,
  InputGroup,
  Badge,
  Dropdown,
  Modal,
  Pagination,
} from "react-bootstrap";
import { gymMembersAPI } from "../../api/gymMembers";
import Swal from "sweetalert2";
import {
  FaSearch,
  FaEdit,
  FaTrash,
  FaEye,
  FaFilter,
  FaFileExport,
  FaUserPlus,
  FaPrint,
  FaDownload,
  FaImage,
} from "react-icons/fa";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "./Members.css";

const Members = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedDate, setSelectedDate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const membersPerPage = 10;

  useEffect(() => {
    fetchMembers();
  }, [currentPage, searchTerm, selectedStatus, selectedDate]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const filters = {
        page: currentPage,
        limit: membersPerPage,
        search: searchTerm,
        status: selectedStatus !== "all" ? selectedStatus : undefined,
        date: selectedDate ? format(selectedDate, "yyyy-MM-dd") : undefined,
      };

      const response = await gymMembersAPI.getMembers(filters);

      // Your API returns data array, not results
      setMembers(response.data.data || []);
      setTotalCount(response.data.count || 0);
      setTotalPages(Math.ceil((response.data.count || 0) / membersPerPage));
    } catch (error) {
      console.error("Error fetching members:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load members",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!memberToDelete) return;

    try {
      await gymMembersAPI.deleteMember(memberToDelete.id);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Member has been deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      fetchMembers();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to delete member",
      });
    } finally {
      setShowDeleteModal(false);
      setMemberToDelete(null);
    }
  };

  const handleExport = async (format = "csv") => {
    try {
      const response = await gymMembersAPI.exportMembers(format);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `members.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error exporting members:", error);
      Swal.fire({
        icon: "error",
        title: "Export Failed",
        text: "Failed to export members data",
      });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge bg="success">Active</Badge>
    ) : (
      <Badge bg="secondary">Inactive</Badge>
    );
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedStatus("all");
    setSelectedDate(null);
    setCurrentPage(1);
  };

  const handleViewDetails = (id) => {
    navigate(`/members/view/${id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd/MM/yyyy");
    } catch (error) {
      return "Invalid Date";
    }
  };

  return (
    <div className="members-page">
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <h2 className="mb-0">Gym Members</h2>
          <div>
            <Button
              variant="primary"
              onClick={() => navigate("/members/add")}
              className="me-2"
            >
              <FaUserPlus className="me-1" /> Add Member
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => navigate("/members/report")}
            >
              <FaFileExport className="me-1" /> Generate Report
            </Button>
          </div>
        </div>
        <p className="text-muted mb-0">Total Members: {totalCount}</p>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by name, email, or mobile..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </InputGroup>
            </Col>
            <Col md={6} className="d-flex justify-content-end">
              <Button
                variant="outline-secondary"
                onClick={() => setShowFilters(!showFilters)}
                className="me-2"
              >
                <FaFilter className="me-1" /> Filters
              </Button>
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary">
                  <FaDownload className="me-1" /> Export
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleExport("csv")}>
                    Export as CSV
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleExport("xlsx")}>
                    Export as Excel
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handlePrint}>
                    <FaPrint className="me-1" /> Print
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>

          {showFilters && (
            <Card className="mb-3">
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        value={selectedStatus}
                        onChange={(e) => handleStatusFilter(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Join Date</Form.Label>
                      <DatePicker
                        selected={selectedDate}
                        onChange={setSelectedDate}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        placeholderText="Select date"
                        isClearable
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="d-flex align-items-end">
                    <Button variant="outline-danger" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="members-table">
                  <thead>
                    <tr>
                      <th>Photo</th>
                      <th>Member ID</th>
                      <th>Name</th>
                      <th>Contact</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Join Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          No members found
                        </td>
                      </tr>
                    ) : (
                      members.map((member) => (
                        <tr key={member.id}>
                          <td>
                            {member.photo ? (
                              <img
                                src={member.photo}
                                alt={`${member.first_name} ${member.last_name}`}
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "50%",
                                  backgroundColor: "#f0f0f0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <FaImage size={20} color="#999" />
                              </div>
                            )}
                          </td>
                          <td>
                            <strong>{member.member_id}</strong>
                          </td>
                          <td>
                            {member.first_name} {member.last_name}
                          </td>
                          <td>{member.mobile_number}</td>
                          <td>{member.email}</td>
                          <td>{getStatusBadge(member.is_active)}</td>
                          <td>{formatDate(member.created_at)}</td>
                          <td>
                            <div className="action-buttons">
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() =>
                                  navigate(`/members/edit/${member.id}`)
                                }
                                className="me-1"
                                title="Edit"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleViewDetails(member.id)}
                                className="me-1"
                                title="View Details"
                              >
                                <FaEye />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteClick(member)}
                                title="Delete"
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    />

                    {[...Array(totalPages).keys()].map((page) => (
                      <Pagination.Item
                        key={page + 1}
                        active={page + 1 === currentPage}
                        onClick={() => setCurrentPage(page + 1)}
                      >
                        {page + 1}
                      </Pagination.Item>
                    ))}

                    <Pagination.Next
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete member{" "}
          <strong>
            {memberToDelete?.first_name} {memberToDelete?.last_name}
          </strong>
          ? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Members;
